// SignupForm Component
// Handles user signup with validation, ReCaptcha, and journey-specific branding

import React, { useEffect, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { RadioGroup, Label, Radio } from '@headlessui/react';
import type { AppDispatch, RootState } from 'app/store';
import { submitSignup, setJourneyConfig } from '../reducers/signupSlice';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { JOURNEY_CONFIGS, DEFAULT_JOURNEY } from '../config/journeyConfig';
import abstractIllustration6 from 'assets/graphics/abstract_illustration_6.svg';
import abstractIllustration18 from 'assets/graphics/abstract_illustration_18.svg';

// Map background image identifiers to imported SVG files
const backgroundImages: Record<string, string> = {
  abstract_illustration_6: abstractIllustration6,
  abstract_illustration_18: abstractIllustration18,
};

const schema = yup
  .object({
    email: yup.string().email('Please enter a valid email address').required('Email is required'),
    name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
    company_name: yup.string().optional(),
    acceptTerms: yup
      .boolean()
      .oneOf([true], 'You must accept the terms and policies to create an account')
      .required('You must accept the terms and policies to create an account'),
  })
  .required();

type SignupFormData = yup.InferType<typeof schema>;

const SignupForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error, fieldErrors, journeyConfig } = useSelector((state: RootState) => state.signup);
  const { getRecaptchaToken } = useRecaptcha();
  const [searchParams, setSearchParams] = useSearchParams();
  const posthog = usePostHog();

  // Get available journeys excluding the default
  const availableJourneys = Object.entries(JOURNEY_CONFIGS).filter(([key]) => key !== DEFAULT_JOURNEY);

  // Track selected journey - null if on default journey (no query param)
  const [selectedJourney, setSelectedJourney] = useState<string | null>(() => {
    const appParam = searchParams.get('app');
    return appParam != null && appParam !== '' && appParam in JOURNEY_CONFIGS && appParam !== DEFAULT_JOURNEY
      ? appParam
      : null;
  });

  // Check if a journey was initially set (for hiding journey selection)
  const [initialJourneySelected] = useState<boolean>(() => {
    const appParam = searchParams.get('app');
    return appParam != null && appParam !== '' && appParam in JOURNEY_CONFIGS && appParam !== DEFAULT_JOURNEY;
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: yupResolver(schema) as any,
  });

  // Apply API field validation errors to form fields
  useEffect(() => {
    if (fieldErrors != null) {
      Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        if (
          fieldName === 'email' ||
          fieldName === 'name' ||
          fieldName === 'company_name' ||
          fieldName === 'acceptTerms'
        ) {
          setError(fieldName, {
            type: 'api',
            message,
          });
        }
      });
    }
  }, [fieldErrors, setError]);

  // Track form errors
  useEffect(() => {
    if (error != null) {
      posthog?.capture('admin_app.frictionless.signup_error', {
        error_message: error,
        app_name: journeyConfig?.app_name ?? 'DataOps.live',
        app_variant: journeyConfig?.app_variant ?? 'default',
      });
    }
  }, [error, journeyConfig, posthog]);

  // Handle journey selection
  const handleJourneyChange = (journeyKey: string | null): void => {
    setSelectedJourney(journeyKey);

    // Update query params
    if (journeyKey != null) {
      setSearchParams({ app: journeyKey });
      // Update journey config in Redux
      dispatch(setJourneyConfig(JOURNEY_CONFIGS[journeyKey]));
    } else {
      // Remove query param and reset to default
      setSearchParams({});
      dispatch(setJourneyConfig(JOURNEY_CONFIGS[DEFAULT_JOURNEY]));
    }
  };

  const onSubmit: SubmitHandler<SignupFormData> = (data) => {
    // Identify user with their email to create person profile
    posthog?.identify(data.email);

    // Track form submission attempt
    posthog?.capture('admin_app.frictionless.signup_submitted', {
      app_name: journeyConfig?.app_name ?? 'DataOps.live',
      app_variant: journeyConfig?.app_variant ?? 'default',
    });

    void getRecaptchaToken('signup')
      .then(async (recaptchaToken) => {
        return await dispatch(
          submitSignup({
            ...data,
            recaptcha_token: recaptchaToken,
            app_name: journeyConfig?.app_name ?? 'DataOps.live',
            app_variant: journeyConfig?.app_variant ?? 'default',
            redirect_to: journeyConfig?.redirect_to ?? '/',
          }),
        ).unwrap();
      })
      .catch((err) => {
        console.error('Signup failed:', err);
      });
  };

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
      className="space-y-6"
    >
      {error != null && fieldErrors == null && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Sign-up failed</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {fieldErrors != null && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Please correct the errors below</h3>
            </div>
          </div>
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email address
        </label>
        <div className="mt-1">
          <input
            {...register('email')}
            id="email"
            type="email"
            autoComplete="email"
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            disabled={isLoading}
          />
          {errors.email != null && <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Full name
        </label>
        <div className="mt-1">
          <input
            {...register('name')}
            id="name"
            type="text"
            autoComplete="name"
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            disabled={isLoading}
          />
          {errors.name != null && <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
          Company name <span className="text-gray-500">(optional)</span>
        </label>
        <div className="mt-1">
          <input
            {...register('company_name')}
            id="company_name"
            type="text"
            autoComplete="organization"
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            disabled={isLoading}
          />
        </div>
      </div>

      {availableJourneys.length > 0 && !initialJourneySelected && (
        <div>
          <RadioGroup value={selectedJourney} onChange={handleJourneyChange}>
            <Label className="block text-sm font-medium text-gray-700 mb-3">
              What would you like to get started with? <span className="text-gray-500">(optional)</span>
            </Label>
            <div className="space-y-2">
              {availableJourneys.map(([key, config]) => (
                <Radio
                  key={key}
                  value={key}
                  disabled={isLoading}
                  className={({ focus, checked }) =>
                    `${focus ? 'ring-2 ring-dataops-primary-blue ring-offset-2' : ''} ${
                      checked ? ' border-transparent' : 'bg-white border-gray-300'
                    } relative flex cursor-pointer rounded-lg border px-5 py-4 shadow-sm focus:outline-none overflow-hidden ${
                      isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`
                  }
                >
                  {({ checked }) => {
                    const backgroundImageSrc =
                      config.backgroundImage != null ? backgroundImages[config.backgroundImage] : null;

                    return (
                      <>
                        {backgroundImageSrc != null && checked && (
                          <div
                            className="absolute inset-0 bg-no-repeat bg-right bg-cover pointer-events-none"
                            style={{
                              backgroundImage: `url(${backgroundImageSrc})`,
                            }}
                          />
                        )}
                        <div className="flex w-full items-center justify-between relative z-10">
                          <div className="flex items-center">
                            <div className="text-sm">
                              <Label as="p" className={`${checked ? 'text-white' : 'text-gray-900'}`}>
                                {config.app_name}
                              </Label>
                              <RadioGroup.Description
                                as="span"
                                className={`inline ${checked ? 'text-white/90' : 'text-gray-500'}`}
                              >
                                {config.description}
                              </RadioGroup.Description>
                            </div>
                          </div>
                          {checked && (
                            <div className="shrink-0 text-white">
                              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                                <circle cx={12} cy={12} r={12} fill="currentColor" opacity="0.2" />
                                <path
                                  d="M7 13l3 3 7-7"
                                  stroke="currentColor"
                                  strokeWidth={1.5}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  }}
                </Radio>
              ))}
            </div>
          </RadioGroup>
        </div>
      )}

      <div>
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              {...register('acceptTerms')}
              id="acceptTerms"
              type="checkbox"
              className="h-4 w-4 text-dataops-primary-blue focus:ring-dataops-primary-blue border-gray-300 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="acceptTerms" className="text-gray-700 cursor-pointer">
              I accept the{' '}
              <a
                href="https://docs.dataops.live/legal-terms/eula/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Terms
              </a>
              ,{' '}
              <a
                href="https://docs.dataops.live/legal-terms/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Privacy Policy
              </a>
              , and{' '}
              <a
                href="https://docs.dataops.live/legal-terms/ai-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                AI Policy
              </a>
            </label>
          </div>
        </div>
        {errors.acceptTerms != null && <p className="mt-2 text-sm text-red-600">{errors.acceptTerms.message}</p>}
      </div>

      <div className="text-xs text-gray-500 space-y-2">
        <p>
          We use{' '}
          <a
            href="https://docs.dataops.live/legal-terms/cookies/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            cookies
          </a>{' '}
          to distinguish you from other users of our website.
        </p>
        <p>
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" className="underline">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" className="underline">
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </div>

      <div>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dataops-secondary-blue hover:bg-dataops-secondary-blue/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Creating account...' : 'Create account'}
        </button>
      </div>
    </form>
  );
};

export default SignupForm;
